/**
 * A tabela do historico de tentativas do conector gov.br.
 *
 * Componente puramente de apresentacao: recebe a pagina, o filtro e o estado de
 * busca por prop, e devolve para quem a chama as duas unicas acoes que cabem
 * aqui — mudar o filtro (que inclui a paginacao) e, no vazio sem filtro, pedir
 * o teste de conexao. Quem busca o dado e o gancho `useTentativas` (dados.ts);
 * quem trata erro de busca e `EstadoDaTela`, que embrulha este componente por
 * fora — por isso ele nunca renderiza um estado de erro proprio.
 *
 * Nao ha linha clicavel nem botao "Ver": a tentativa nao tem tela de detalhe
 * (CLAUDE.md, "Regras de interface", item 9 fala da listagem que TEM detalhe —
 * esta nao tem, entao a regra nao se aplica, e inventar uma acao so pareceria
 * fazer algo).
 */

import { type ReactElement } from 'react';

import { DataTable, type DataTableColumn } from '@/ui/DataTable';
import { StatusBadge } from '@/ui/StatusBadge';
import { Label } from '@/ui/base/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';

import {
  AMBIENTES,
  ROTULO_DO_AMBIENTE,
  dataHoraLegivel,
  duracaoLegivel,
  rotuloDaEtapa,
  type Ambiente,
  type FiltroTentativas,
  type PaginaTentativas,
  type TentativaGovbr,
} from './contrato';

/**
 * O `Select` do Radix reserva string vazia para "sem selecao" — nao aceita como
 * valor de item. "Todos" precisa, entao, de um valor proprio que nao colida com
 * nenhum codigo real de ambiente.
 */
const TODOS_OS_AMBIENTES = 'TODOS';

export interface TabelaDeTentativasProps {
  /** A página atual do histórico. Ausente enquanto a primeira busca não voltou. */
  pagina: PaginaTentativas | undefined;
  /** Busca em andamento. */
  carregando: boolean;
  filtro: FiltroTentativas;
  aoMudarFiltro: (filtro: FiltroTentativas) => void;
  /**
   * Ação do estado vazio: leva a pessoa a rodar o teste de conexão, que é o que
   * produz a primeira tentativa. Quando ausente, o estado vazio não oferece ação.
   */
  aoTestarConexao?: () => void;
}

export function TabelaDeTentativas({
  pagina,
  carregando,
  filtro,
  aoMudarFiltro,
  aoTestarConexao,
}: TabelaDeTentativasProps): ReactElement {
  const haFiltroDeAmbiente = filtro.ambiente !== undefined;

  const colunas: DataTableColumn<TentativaGovbr>[] = [
    {
      id: 'data',
      header: 'Data e hora',
      cell: (linha) => dataHoraLegivel(linha.created_at),
      cellClassName: 'whitespace-nowrap',
    },
    {
      id: 'ambiente',
      header: 'Ambiente',
      // `ambiente` e texto livre no banco, nao o tipo fechado `Ambiente` — o
      // codigo cru como ultimo recurso cobre o valor que a lista da tela nao
      // conhece, do mesmo jeito que `rotuloDaEtapa` faz para a etapa.
      cell: (linha) => ROTULO_DO_AMBIENTE[linha.ambiente as Ambiente] ?? linha.ambiente,
      cellClassName: 'whitespace-nowrap',
    },
    {
      id: 'etapa',
      header: 'Etapa',
      cell: (linha) => rotuloDaEtapa(linha.etapa),
      cellClassName: 'whitespace-nowrap',
    },
    {
      id: 'resultado',
      header: 'Resultado',
      cell: (linha) =>
        linha.sucesso ? (
          <StatusBadge status="aprovado" label="Sucesso" />
        ) : (
          <StatusBadge status="rejeitado" label="Falha" />
        ),
      cellClassName: 'whitespace-nowrap',
    },
    {
      id: 'motivo',
      header: 'Motivo',
      // `codigo_erro` e codigo de maquina (ex.: SEM_S256): nunca aparece como
      // texto da celula (CLAUDE.md, "Regras de interface", item 8). Ele so
      // existe, discreto, no `title` de quem passar o mouse — a mensagem em
      // portugues, que a API ja grava pronta, e o que a celula mostra. Tentativa
      // bem-sucedida nao tem motivo: mostra "—", nunca celula vazia.
      cell: (linha) => <span title={linha.codigo_erro ?? undefined}>{linha.mensagem ?? '—'}</span>,
    },
    {
      id: 'duracao',
      header: 'Duração',
      cell: (linha) => duracaoLegivel(linha.duracao_ms),
      cellClassName: 'whitespace-nowrap',
    },
  ];

  function mudarPagina(novaPagina: number): void {
    aoMudarFiltro({ ...filtro, pagina: novaPagina });
  }

  function mudarTamanho(novoTamanho: number): void {
    // Tamanho de pagina novo muda o que cabe em cada pagina: sem voltar para a
    // 1, a pessoa pode ficar numa pagina que deixou de existir no novo recorte.
    aoMudarFiltro({ ...filtro, tamanho: novoTamanho, pagina: 1 });
  }

  function mudarAmbiente(valor: string): void {
    // Trocar o filtro sempre volta para a pagina 1 — o mesmo motivo do tamanho
    // de pagina: a pagina atual pode nao existir no recorte novo.
    aoMudarFiltro({
      ...filtro,
      ambiente: valor === TODOS_OS_AMBIENTES ? undefined : (valor as Ambiente),
      pagina: 1,
    });
  }

  function limparFiltroDeAmbiente(): void {
    aoMudarFiltro({ ...filtro, ambiente: undefined, pagina: 1 });
  }

  return (
    <div className="space-y-3">
      <div className="max-w-[16rem] space-y-1">
        <Label htmlFor="tabela-tentativas-filtro-ambiente">Ambiente</Label>
        <Select value={filtro.ambiente ?? TODOS_OS_AMBIENTES} onValueChange={mudarAmbiente}>
          <SelectTrigger
            id="tabela-tentativas-filtro-ambiente"
            aria-label="Filtrar histórico por ambiente"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={TODOS_OS_AMBIENTES}>Todos</SelectItem>
            {AMBIENTES.map((ambiente) => (
              <SelectItem key={ambiente} value={ambiente}>
                {ROTULO_DO_AMBIENTE[ambiente]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <DataTable<TentativaGovbr>
        columns={colunas}
        data={pagina?.itens ?? []}
        // O esqueleto so entra quando ainda nao ha NENHUM dado na tela — ou
        // seja, na primeira busca. Numa troca de pagina ou de filtro ja ha uma
        // pagina anterior na tela, e `useTentativas` a mantem visivel enquanto a
        // proxima chega (dados.ts, `placeholderData`). Se o esqueleto tambem
        // entrasse nesse caso, a tabela desapareceria e voltaria vazia por um
        // instante, e a pagina saltaria de altura a cada clique — exatamente o
        // que `placeholderData` existe para evitar.
        isLoading={carregando && pagina === undefined}
        total={pagina?.total ?? 0}
        page={pagina?.pagina ?? filtro.pagina}
        perPage={pagina?.tamanho ?? filtro.tamanho}
        onPageChange={mudarPagina}
        onPerPageChange={mudarTamanho}
        minWidth="880px"
        ariaLabel="Histórico de tentativas de integração com o gov.br"
        skeletonColumns={colunas.length}
        emptyTitle={
          haFiltroDeAmbiente
            ? 'Nenhuma tentativa neste ambiente'
            : 'Nenhuma integração registrada ainda'
        }
        emptyDescription={
          haFiltroDeAmbiente
            ? 'Não há tentativa registrada para o ambiente escolhido. Limpe o filtro para ver as demais.'
            : 'Ainda não há tentativa de integração registrada. Rode o teste de conexão para registrar a primeira.'
        }
        // "Testar a conexão agora", e nao "Testar conexão": o formulario acima
        // ja tem um botao com esse nome exato, e dois controles com o mesmo
        // nome acessivel na mesma pagina obrigam quem usa leitor de tela a
        // adivinhar qual e qual. A acao e a mesma; o nome, nao pode ser.
        emptyActionLabel={haFiltroDeAmbiente ? 'Limpar filtro' : 'Testar a conexão agora'}
        onEmptyAction={haFiltroDeAmbiente ? limparFiltroDeAmbiente : aoTestarConexao}
      />
    </div>
  );
}
